import Image from 'next/image';
import React from 'react';
import { Rating } from 'react-simple-star-rating';

interface ProductReviewCardProps {
  name: string;
  rating: number;
  timeAgo: string;
  review: string;
  onSeeMore?: () => void;
  height?: string;
}

const avatarColors = [
  '#FFF3D1',
  '#E3E6F0',
  '#FDE2E4',
  '#D1F7FF',
  '#E2F7D1',
  '#F7E2D1',
  '#E2D1F7'
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

const getInitial = (name: string) => name.charAt(0).toUpperCase();

export const ProductReviewCard: React.FC<ProductReviewCardProps> = ({
  name,
  rating,
  timeAgo,
  review,
  height,
  onSeeMore
}) => {
  const isLong = review?.length > 205;
  const displayText = !isLong ? review : review.slice(0, 205);

  return (
    <div
      className={`bg-[#F7F8FB] rounded-2xl break-words ${height} p-6 lg:p-2 xl:p-6`}
    >
      <div className="flex items-center mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-lg text-[#4B5C6B] mr-4"
          style={{ backgroundColor: getAvatarColor(name) }}
        >
          {getInitial(name)}
        </div>
        <div className="flex-1">
          <div className="font-semibold text-base text-primary">{name}</div>
          <div className="flex flex-col lg:items-start gap-1 mt-0.5">
            <div className="flex items-center">
              <div className="md:block hidden">
                <Rating
                  initialValue={rating}
                  readonly
                  allowFraction
                  size={18}
                  fillColor="#FFD600"
                  emptyColor="#E3E6F0"
                  SVGstyle={{ display: 'inline-block' }}
                />
              </div>
              <div className="md:hidden block">
                <Image
                  src={'/homestar.svg'}
                  width={14}
                  height={14}
                  alt="rating-star"
                  className="mb-[2px]"
                />
              </div>
              <span className="font-semibold text-sm text-primary ml-1">
                {rating}/5
              </span>
              <span className="text-xs text-[#62646A] ml-2 block lg:hidden xl:block">
                {timeAgo}
              </span>
            </div>
            <span className="text-xs text-[#62646A] hidden lg:block xl:hidden xl:ml-0 w-full">
              {timeAgo}
            </span>
          </div>
        </div>
      </div>
      <div className="text-primary text-[14px] md:text-base leading-relaxed text-justify">
        {displayText}
        {isLong && (
          <>
            <span>....</span>
            <span
              className="font-semibold cursor-pointer ml-1 text-primary"
              onClick={onSeeMore}
            >
              see more
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductReviewCard;
